import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Music, Play, Pause, Mail, FileText, Filter, Search, Volume2, ArrowLeft, MessageCircle, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const GalleryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [works, setWorks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [showContactForm, setShowContactForm] = useState(null);
  const audioRef = useRef(null);
  
  // Busca Inteligente
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('normal'); // 'normal' ou 'semantic'
  const [semanticResults, setSemanticResults] = useState([]);
  const [searchingSemantics, setSearchingSemantics] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    visitor_name: '',
    visitor_email: '',
    message: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Voltar para a página anterior usando histórico do navegador
  const handleBackNavigation = () => {
    navigate(-1);
  };

  useEffect(() => {
    loadGenres();
    loadWorks();
  }, [selectedGenre]);

  const loadGenres = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery/genres`);
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Erro ao carregar gêneros:', error);
    }
  };

  const loadWorks = async () => {
    setLoading(true);
    try {
      const params = selectedGenre ? { genre: selectedGenre } : {};
      const response = await axios.get(`${BACKEND_URL}/api/gallery/works`, { params });
      setWorks(response.data.works || []);
    } catch (error) {
      console.error('Erro ao carregar músicas:', error);
      toast.error('Erro ao carregar músicas');
    } finally {
      setLoading(false);
    }
  };

  const handleSemanticSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      toast.error('Digite pelo menos 3 caracteres para buscar');
      return;
    }

    setSearchingSemantics(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gallery/semantic-search`, null, {
        params: {
          query: searchQuery,
          genre: selectedGenre || null,
          limit: 20
        }
      });
      
      setSemanticResults(response.data.results || []);
      setSearchMode('semantic');
      toast.success(`${response.data.results_count} músicas encontradas!`);
      
    } catch (error) {
      console.error('Erro na busca semântica:', error);
      toast.error('Erro ao realizar busca inteligente');
    } finally {
      setSearchingSemantics(false);
    }
  };

  const clearSemanticSearch = () => {
    setSearchMode('normal');
    setSearchQuery('');
    setSemanticResults([]);
  };

  const handlePlay = (workId) => {
    if (currentPlaying === workId) {
      // Pausar
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setCurrentPlaying(null);
    } else {
      // Tocar nova música
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setCurrentPlaying(workId);
      
      // Criar novo elemento de áudio
      const audio = new Audio(`${BACKEND_URL}/api/gallery/audio/${workId}`);
      audioRef.current = audio;
      
      audio.play().catch(err => {
        console.error('Erro ao tocar áudio:', err);
        toast.error('Erro ao reproduzir áudio');
        setCurrentPlaying(null);
      });

      audio.onended = () => {
        setCurrentPlaying(null);
      };
    }
  };

  const handleContact = async (workId) => {
    if (!contactForm.visitor_name || !contactForm.visitor_email || !contactForm.message) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/gallery/contact`, {
        work_id: workId,
        ...contactForm
      });
      
      toast.success('Mensagem enviada com sucesso!');
      setShowContactForm(null);
      setContactForm({ visitor_name: '', visitor_email: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar contato:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Botão Voltar */}
          <button 
            onClick={handleBackNavigation}
            className="inline-flex items-center gap-2 text-white hover:text-orange-200 transition-colors mb-6 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Voltar</span>
          </button>
          
          <div className="text-center">
            <Volume2 className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('protectionSystem.musicalGalleryTitle')}
            </h1>
            <p className="text-xl text-orange-100 mb-2">
              {t('protectionSystem.listenToUnreleased')}
            </p>
            <p className="text-orange-200">
              {t('protectionSystem.discoverTalents')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Busca Inteligente */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full filter blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">🔍</span>
              <h2 className="text-2xl font-bold">Busca Inteligente por Trechos</h2>
            </div>
            <p className="text-purple-100 mb-6">
              Digite trechos como: "coração partido madrugada", "deixa no vácuo", "plano B"
            </p>
            
            <div className="flex gap-4 flex-col md:flex-row">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSemanticSearch()}
                  placeholder="Ex: coração partido madrugada cerveja..."
                  className="w-full px-6 py-4 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-purple-300"
                />
              </div>
              <button
                onClick={handleSemanticSearch}
                disabled={searchingSemantics}
                className="bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {searchingSemantics ? '🔄 Buscando...' : '🚀 Buscar'}
              </button>
            </div>
            
            {searchMode === 'semantic' && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-purple-100">
                  ✨ {semanticResults.length} resultado(s) encontrado(s)
                </span>
                <button
                  onClick={clearSemanticSearch}
                  className="text-purple-100 hover:text-white underline"
                >
                  Limpar busca
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-700">Filtrar por gênero:</span>
            
            <button
              onClick={() => setSelectedGenre('')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedGenre === '' 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            
            {genres.map(genre => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedGenre === genre 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de Músicas */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando músicas...</p>
          </div>
        ) : (searchMode === 'semantic' && semanticResults.length === 0) ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhum resultado encontrado
            </h3>
            <p className="text-gray-500">
              Tente buscar com outras palavras ou trechos de letra
            </p>
          </div>
        ) : (searchMode === 'normal' && works.length === 0) ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhuma música encontrada
            </h3>
            <p className="text-gray-500">
              {selectedGenre 
                ? `Não há músicas do gênero "${selectedGenre}" na galeria ainda.`
                : 'Não há músicas na galeria ainda.'
              }
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Resultados Semânticos */}
            {searchMode === 'semantic' && semanticResults.map(result => {
              // Encontrar a obra completa nos works
              const work = works.find(w => w.id === result.work_id) || {
                id: result.work_id,
                title: result.title,
                author_name: result.author_name,
                genre: result.genre
              };
              
              return (
              <div
                key={result.work_id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:scale-105 border-2 border-purple-300"
              >
                {/* Badge de Similaridade */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold">{result.similarity.toFixed(0)}%</span>
                    <span className="text-sm">de similaridade</span>
                  </div>
                </div>

                {/* Capa da Música */}
                <div className="relative aspect-square bg-gradient-to-br from-purple-500 to-pink-500">
                  {work.cover_image_id ? (
                    <img 
                      src={`${BACKEND_URL}/api/works/${work.id}/cover`}
                      alt={`Capa de ${work.title}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div 
                    className={`absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center ${work.cover_image_id ? 'hidden' : 'flex'}`}
                  >
                    <div className="text-white text-center p-6">
                      <Music className="w-16 h-16 mx-auto mb-4 opacity-80" />
                      <h3 className="text-2xl font-bold mb-2">{result.title}</h3>
                      <p className="text-purple-100">{result.genre || 'Sem gênero'}</p>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{result.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">Por {result.author_name}</p>
                  
                  {/* Trecho correspondente */}
                  <div className="bg-purple-50 border-l-4 border-purple-600 p-3 rounded mb-4">
                    <p className="text-xs text-purple-600 font-semibold mb-1">📝 Trecho correspondente:</p>
                    <p className="text-sm text-gray-700 italic line-clamp-3">
                      "{result.matching_chunk}"
                    </p>
                  </div>

                  {/* Botões */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handlePlay(work.id)}
                      className={`py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                        currentPlaying === work.id
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                      }`}
                    >
                      {currentPlaying === work.id ? (
                        <><Pause className="w-5 h-5" /><span>Pausar</span></>
                      ) : (
                        <><Play className="w-5 h-5" /><span>Ouvir</span></>
                      )}
                    </button>
                    
                    <a
                      href={`${BACKEND_URL}/api/gallery/lyrics/${work.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={async (e) => {
                        toast.success('Abrindo letra da música...');
                      }}
                      className="bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FileText className="w-5 h-5" />
                      <span>Letra</span>
                    </a>
                  </div>
                </div>
              </div>
            )})}

            {/* Obras Normais */}
            {searchMode === 'normal' && works.map(work => (
              <div
                key={work.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:scale-105"
              >
                {/* Capa da Música */}
                <div className="relative aspect-square bg-gradient-to-br from-purple-500 to-pink-500">
                  {work.cover_image_id ? (
                    <img 
                      src={`${BACKEND_URL}/api/works/${work.id}/cover`}
                      alt={`Capa de ${work.title}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback se imagem não carregar
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  {/* Fallback se não tiver capa */}
                  <div 
                    className={`absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center ${work.cover_image_id ? 'hidden' : 'flex'}`}
                  >
                    <div className="text-white text-center p-6">
                      <Music className="w-16 h-16 mx-auto mb-4 opacity-80" />
                      <h3 className="text-2xl font-bold mb-2">{work.title}</h3>
                      <p className="text-orange-100">{work.genre || 'Sem gênero'}</p>
                    </div>
                  </div>
                  
                  {/* Badge do Gênero sobreposto */}
                  <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
                    <p className="text-white text-xs font-semibold">{work.genre || 'Música'}</p>
                  </div>
                  
                  {/* Botão Play Sobreposto */}
                  <button
                    onClick={() => handlePlay(work.id)}
                    className={`absolute bottom-3 right-3 p-4 rounded-full shadow-lg transition-all transform hover:scale-110 ${
                      currentPlaying === work.id
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                  >
                    {currentPlaying === work.id ? (
                      <Pause className="w-6 h-6 text-white" />
                    ) : (
                      <Play className="w-6 h-6 text-white" />
                    )}
                  </button>
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-4">
                  {/* Título e Autor */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1 truncate">{work.title}</h3>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">🎤</span> {work.author_name}
                    </p>
                  </div>

                  {/* Botões de Ação - 2 botões em linha */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Botão Ver Letra PDF */}
                    <button
                      onClick={async () => {
                        const pdfUrl = `${BACKEND_URL}/api/gallery/lyrics/${work.id}`;
                        
                        try {
                          toast.loading('Carregando letra da música...');
                          
                          // Fazer download do PDF via fetch
                          const response = await axios.get(pdfUrl, {
                            responseType: 'blob'
                          });
                          
                          // Criar blob URL
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const blobUrl = URL.createObjectURL(blob);
                          
                          // Abrir em nova aba
                          const newWindow = window.open(blobUrl, '_blank');
                          
                          if (newWindow) {
                            toast.dismiss();
                            toast.success('Letra aberta em nova aba!');
                          } else {
                            toast.dismiss();
                            toast.error('Pop-ups bloqueados. Por favor, permita pop-ups para este site.');
                          }
                          
                        } catch (error) {
                          toast.dismiss();
                          console.error('Erro ao carregar letra:', error);
                          toast.error('Erro ao carregar letra. Tente novamente.');
                        }
                      }}
                      className="bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FileText className="w-5 h-5" />
                      <span>Letra</span>
                    </button>
                    
                    {/* Botão WhatsApp (se tiver telefone) */}
                    {work.author_phone && (
                      <a
                        href={`https://wa.me/${work.author_phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        title="Falar no WhatsApp"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>WhatsApp</span>
                      </a>
                    )}
                    
                    {/* Botão Falar com Autor (Email) */}
                    <button
                      onClick={() => setShowContactForm(showContactForm === work.id ? null : work.id)}
                      className="bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      title="Enviar email"
                    >
                      <Mail className="w-5 h-5" />
                      <span>E-mail</span>
                    </button>
                  </div>

                  {/* Formulário de Contato */}
                  {showContactForm === work.id && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-3">
                      <h4 className="font-semibold text-green-900 mb-2">
                        Falar com {work.author_name}
                      </h4>
                      
                      <input
                        type="text"
                        placeholder="Seu nome"
                        value={contactForm.visitor_name}
                        onChange={(e) => setContactForm({...contactForm, visitor_name: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      
                      <input
                        type="email"
                        placeholder="Seu email"
                        value={contactForm.visitor_email}
                        onChange={(e) => setContactForm({...contactForm, visitor_email: e.target.value})}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      
                      <textarea
                        placeholder="Sua mensagem..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        rows="3"
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      />
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleContact(work.id)}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                        >
                          Enviar
                        </button>
                        <button
                          onClick={() => setShowContactForm(null)}
                          className="px-4 bg-gray-200 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA para registrar música */}
        <div className="mt-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">
            Quer divulgar sua música aqui?
          </h3>
          <p className="text-orange-100 mb-6 max-w-2xl mx-auto">
            Registre suas obras na plataforma e marque a opção "Divulgar na Galeria Pública" 
            para que sua música apareça aqui e alcance novos ouvintes!
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-orange-600 px-8 py-3 rounded-lg font-bold hover:bg-orange-50 transition-colors"
          >
            Registrar Minha Música
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
